import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { Box, Typography, Tooltip, Divider, Chip } from '@mui/material';
import { format, parseISO, isValid, addMonths, startOfMonth } from 'date-fns';
import type { Contract } from '../../types';
import { contractsApi } from '../../services/api';

interface Amendment {
  id: string;
  amendment_number: number;
  amendment_name: string;
  effective_date?: string;
  created_at: string;
  description: string;
  changed_fields: string;
}

interface ContractGanttProps {
  contract: Contract;
  amendments: Amendment[];
}

const STATUS_COLOR: Record<string, string> = {
  active:     '#2e7d32',
  expired:    '#c62828',
  draft:      '#9e9e9e',
  terminated: '#b71c1c',
  suspended:  '#e65100',
};

function parseDate(d?: string | null): Date | null {
  if (!d) return null;
  const p = parseISO(d);
  return isValid(p) ? p : null;
}

// Key contract dates to show as labeled ticks
function getContractTicks(contract: Contract) {
  const ticks: { date: Date; label: string; color: string; position: 'above' | 'below' }[] = [];
  const add = (d: string | undefined | null, label: string, color: string, position: 'above' | 'below') => {
    const p = parseDate(d);
    if (p) ticks.push({ date: p, label, color, position });
  };
  add(contract.execution_date,     'Signed',          '#1565c0', 'above');
  add(contract.effective_date,     'Effective',       '#2e7d32', 'above');
  add(contract.start_date,         'Start',           '#00695c', 'below');
  add(contract.renewal_notice_date,'Notice Deadline', '#e65100', 'below');
  add(contract.end_date,           'End',             '#6a1b9a', 'above');
  add(contract.expiration_date,    'Expires',         '#c62828', 'below');
  return ticks;
}

export default function ContractGantt({ contract, amendments }: ContractGanttProps) {
  const today = useMemo(() => new Date(), []);
  const barColor = STATUS_COLOR[contract.status] ?? '#1a6fff';

  // Fetch audit history to render edit ticks on the timeline
  const [editGroups, setEditGroups] = useState<{ date: Date; dateKey: string; entries: any[] }[]>([]);
  useEffect(() => {
    contractsApi.history(contract.id).then(res => {
      const updates: any[] = (res.data || []).filter((e: any) => e.action === 'UPDATE');
      // Group by calendar date (YYYY-MM-DD)
      const byDay: Record<string, any[]> = {};
      updates.forEach(e => {
        const key = e.created_at.slice(0, 10);
        if (!byDay[key]) byDay[key] = [];
        byDay[key].push(e);
      });
      setEditGroups(
        Object.entries(byDay).map(([dateKey, entries]) => ({
          dateKey,
          date: parseISO(dateKey),
          entries
        }))
      );
    }).catch(() => {});
  }, [contract.id]);

  // Collect all dates to compute axis range
  const { minDate, maxDate } = useMemo(() => {
    const dates: Date[] = [today];
    [
      contract.execution_date, contract.effective_date, contract.start_date,
      contract.expiration_date, contract.end_date, contract.renewal_notice_date
    ].forEach(d => { const p = parseDate(d); if (p) dates.push(p); });

    amendments.forEach(a => {
      const p = parseDate(a.effective_date || a.created_at);
      if (p) dates.push(p);
    });

    editGroups.forEach(g => { if (isValid(g.date)) dates.push(g.date); });

    const minMs = Math.min(...dates.map(d => d.getTime()));
    const maxMs = Math.max(...dates.map(d => d.getTime()));
    const pad   = Math.max((maxMs - minMs) * 0.08, 1000 * 60 * 60 * 24 * 45); // min 45 day pad
    return { minDate: new Date(minMs - pad), maxDate: new Date(maxMs + pad) };
  }, [contract, amendments, editGroups, today]);

  const totalMs = maxDate.getTime() - minDate.getTime();
  const toPercent = useCallback(
    (date: Date) => Math.max(0, Math.min(100, ((date.getTime() - minDate.getTime()) / totalMs) * 100)),
    [minDate, totalMs]
  );

  // Month/year axis ticks
  const axisTicks = useMemo(() => {
    const rangeYears = totalMs / (1000 * 60 * 60 * 24 * 365);
    const step = rangeYears > 4 ? 6 : rangeYears > 2 ? 3 : 1;
    const ticks: { label: string; pct: number }[] = [];
    let d = startOfMonth(minDate);
    while (d <= maxDate) {
      const pct = ((d.getTime() - minDate.getTime()) / totalMs) * 100;
      if (pct >= 0 && pct <= 100) {
        ticks.push({
          label: rangeYears > 3 ? format(d, 'MMM yy') : d.getMonth() === 0 ? format(d, 'MMM yyyy') : format(d, 'MMM'),
          pct
        });
      }
      d = addMonths(d, step);
    }
    return ticks;
  }, [minDate, maxDate, totalMs]);

  const todayPct = ((today.getTime() - minDate.getTime()) / totalMs) * 100;

  // Contract bar extents
  const barStartDate = parseDate(contract.effective_date) || parseDate(contract.start_date) || parseDate(contract.execution_date);
  const barEndDate   = parseDate(contract.expiration_date) || parseDate(contract.end_date);
  const barLeft  = barStartDate ? toPercent(barStartDate) : 0;
  const barRight = barEndDate   ? toPercent(barEndDate)   : 100;
  const barWidth = Math.max(0.5, barRight - barLeft);

  const contractTicks = getContractTicks(contract);

  const noDateInfo = !barStartDate && !barEndDate;

  if (noDateInfo) {
    return (
      <Box py={6} textAlign="center">
        <Typography color="text.secondary">
          Add effective and expiration dates to this contract to see the timeline.
        </Typography>
      </Box>
    );
  }

  const TIMELINE_HEIGHT = 80;   // px for the bar row
  const AXIS_HEIGHT     = 36;   // px for bottom axis

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', pb: 1 }}>

        {/* ── Axis header ── */}
        <Box sx={{ position: 'relative', height: AXIS_HEIGHT, borderBottom: '2px solid', borderColor: 'divider', mb: 0 }}>
          {axisTicks.map((tick, i) => (
            <Box key={i} sx={{ position: 'absolute', left: `${tick.pct}%`, top: 0, bottom: 0 }}>
              <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 1, bgcolor: 'divider' }} />
              <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', bottom: 6, left: 4, fontSize: 10, whiteSpace: 'nowrap', fontWeight: 500 }}>
                {tick.label}
              </Typography>
            </Box>
          ))}
          {todayPct >= 0 && todayPct <= 100 && (
            <Box sx={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, width: 2, bgcolor: 'error.main', zIndex: 2 }}>
              <Typography variant="caption" sx={{ position: 'absolute', top: 4, left: 4, fontSize: 9, color: 'error.main', fontWeight: 700, whiteSpace: 'nowrap' }}>
                TODAY
              </Typography>
            </Box>
          )}
        </Box>

        {/* ── Timeline row ── */}
        <Box sx={{ position: 'relative', height: TIMELINE_HEIGHT, bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>

          {/* Grid lines */}
          {axisTicks.map((tick, i) => (
            <Box key={i} sx={{ position: 'absolute', left: `${tick.pct}%`, top: 0, bottom: 0, width: 1, bgcolor: 'divider', opacity: 0.5 }} />
          ))}

          {/* Today line */}
          {todayPct >= 0 && todayPct <= 100 && (
            <Box sx={{ position: 'absolute', left: `${todayPct}%`, top: 0, bottom: 0, width: 2, bgcolor: 'error.main', opacity: 0.3, zIndex: 1 }} />
          )}

          {/* Contract bar */}
          <Tooltip
            title={`${contract.contract_name}${barStartDate && barEndDate ? ` · ${format(barStartDate, 'MMM d, yyyy')} – ${format(barEndDate, 'MMM d, yyyy')}` : ''}`}
            arrow
          >
            <Box sx={{
              position: 'absolute',
              left: `${barLeft}%`,
              width: `${barWidth}%`,
              top: '50%',
              transform: 'translateY(-50%)',
              height: 28,
              bgcolor: barColor,
              borderRadius: 1.5,
              opacity: 0.85,
              zIndex: 2,
              boxShadow: '0 2px 6px rgba(0,0,0,0.18)',
              display: 'flex',
              alignItems: 'center',
              px: 1,
              overflow: 'hidden'
            }}>
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {contract.contract_type} · {contract.contract_name}
              </Typography>
            </Box>
          </Tooltip>

          {/* Key date ticks — above the bar */}
          {contractTicks.filter(t => t.position === 'above').map((tick, i) => {
            const pct = toPercent(tick.date);
            return (
              <Tooltip key={i} title={`${tick.label}: ${format(tick.date, 'MMM d, yyyy')}`} arrow placement="top">
                <Box sx={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: '50%', zIndex: 3, transform: 'translateX(-50%)' }}>
                  {/* Vertical stem */}
                  <Box sx={{ position: 'absolute', left: '50%', top: 6, bottom: 6, width: 1.5, bgcolor: tick.color, opacity: 0.7 }} />
                  {/* Diamond marker */}
                  <Box sx={{ position: 'absolute', top: 2, left: '50%', transform: 'translate(-50%, 0) rotate(45deg)', width: 8, height: 8, bgcolor: tick.color, border: '1.5px solid white', boxShadow: 1 }} />
                </Box>
              </Tooltip>
            );
          })}

          {/* Key date ticks — below the bar */}
          {contractTicks.filter(t => t.position === 'below').map((tick, i) => {
            const pct = toPercent(tick.date);
            return (
              <Tooltip key={i} title={`${tick.label}: ${format(tick.date, 'MMM d, yyyy')}`} arrow placement="bottom">
                <Box sx={{ position: 'absolute', left: `${pct}%`, top: '50%', bottom: 0, zIndex: 3, transform: 'translateX(-50%)' }}>
                  <Box sx={{ position: 'absolute', left: '50%', top: 6, bottom: 6, width: 1.5, bgcolor: tick.color, opacity: 0.7 }} />
                  <Box sx={{ position: 'absolute', bottom: 2, left: '50%', transform: 'translate(-50%, 0) rotate(45deg)', width: 8, height: 8, bgcolor: tick.color, border: '1.5px solid white', boxShadow: 1 }} />
                </Box>
              </Tooltip>
            );
          })}

          {/* Edit ticks — one marker per unique day edits occurred */}
          {editGroups.map((group) => {
            if (!isValid(group.date)) return null;
            const pct = toPercent(group.date);
            if (pct < 0 || pct > 100) return null;
            const count = group.entries.length;
            // Collect changed field labels for tooltip
            const allChangedLabels = group.entries.flatMap((e: any) =>
              (e.changes || []).map((c: any) => c.label)
            );
            const uniqueLabels = Array.from(new Set(allChangedLabels));
            const editors = Array.from(new Set(group.entries.map((e: any) => e.user_name).filter(Boolean)));
            return (
              <Tooltip
                key={group.dateKey}
                arrow
                placement="top"
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={700} display="block">
                      {count} edit{count !== 1 ? 's' : ''} · {format(group.date, 'MMM d, yyyy')}
                    </Typography>
                    {editors.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                        By: {editors.join(', ')}
                      </Typography>
                    )}
                    {uniqueLabels.length > 0 && (
                      <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.25 }}>
                        Fields: {uniqueLabels.slice(0, 6).join(', ')}{uniqueLabels.length > 6 ? ` +${uniqueLabels.length - 6} more` : ''}
                      </Typography>
                    )}
                  </Box>
                }
              >
                <Box sx={{ position: 'absolute', left: `${pct}%`, top: 0, bottom: '50%', zIndex: 4, transform: 'translateX(-50%)' }}>
                  {/* Stem */}
                  <Box sx={{ position: 'absolute', left: '50%', top: 4, bottom: 4, width: 1.5, bgcolor: '#f57c00', opacity: 0.8, transform: 'translateX(-50%)' }} />
                  {/* Triangle marker at top */}
                  <Box sx={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 0, height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '10px solid #f57c00',
                    filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))'
                  }} />
                  {/* Count badge — only when more than 1 edit on this day */}
                  {count > 1 && (
                    <Box sx={{
                      position: 'absolute',
                      top: 10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      bgcolor: '#f57c00',
                      color: 'white',
                      fontSize: 8,
                      fontWeight: 700,
                      lineHeight: 1,
                      px: '3px',
                      py: '1px',
                      borderRadius: '3px',
                      whiteSpace: 'nowrap'
                    }}>
                      {count}
                    </Box>
                  )}
                </Box>
              </Tooltip>
            );
          })}

          {/* Amendment ticks */}
          {amendments.map((a) => {
            const aDate = parseDate(a.effective_date || a.created_at);
            if (!aDate) return null;
            const pct = ((aDate.getTime() - minDate.getTime()) / totalMs) * 100;
            if (pct < 0 || pct > 100) return null;
            return (
              <Tooltip
                key={a.id}
                title={
                  <Box>
                    <Typography variant="caption" fontWeight={700} display="block">{a.amendment_name}</Typography>
                    <Typography variant="caption" display="block">{format(aDate, 'MMM d, yyyy')}</Typography>
                    <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>{a.description}</Typography>
                  </Box>
                }
                arrow
                placement="top"
              >
                <Box sx={{ position: 'absolute', left: `${pct}%`, top: '50%', transform: 'translate(-50%, -50%)', zIndex: 5 }}>
                  {/* Circle with amendment number */}
                  <Box sx={{
                    width: 20, height: 20,
                    borderRadius: '50%',
                    bgcolor: '#6a1b9a',
                    border: '2px solid white',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <Typography sx={{ color: 'white', fontSize: 9, fontWeight: 700, lineHeight: 1 }}>
                      A{a.amendment_number}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        {/* ── Tick label strip ── */}
        <Box sx={{ position: 'relative', height: 44 }}>
          {contractTicks.map((tick, i) => {
            const pct = toPercent(tick.date);
            return (
              <Box key={i} sx={{ position: 'absolute', left: `${pct}%`, top: 6, transform: 'translateX(-50%)', textAlign: 'center' }}>
                <Typography variant="caption" sx={{ fontSize: 9, color: tick.color, fontWeight: 700, whiteSpace: 'nowrap', display: 'block' }}>
                  {tick.label}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: 9, color: 'text.disabled', whiteSpace: 'nowrap', display: 'block' }}>
                  {format(tick.date, 'MMM d')}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── Amendment list ── */}
        {amendments.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, letterSpacing: 0.5 }}>
              AMENDMENTS ON TIMELINE
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {amendments.map(a => {
                const aDate = parseDate(a.effective_date || a.created_at);
                return (
                  <Chip
                    key={a.id}
                    label={`A${a.amendment_number} · ${a.amendment_name}${aDate ? ' · ' + format(aDate, 'MMM d, yyyy') : ''}`}
                    size="small"
                    sx={{ bgcolor: '#6a1b9a', color: 'white', fontSize: 10 }}
                  />
                );
              })}
            </Box>
          </>
        )}

        {/* ── Legend ── */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {[
            { label: 'Signed / Effective / Start / End / Expires / Notice', icon: <Box sx={{ width: 8, height: 8, bgcolor: 'grey.600', transform: 'rotate(45deg)', border: '1px solid #ccc', flexShrink: 0, mt: '1px' }} /> },
          ].map((item, i) => (
            <Box key={i} display="flex" alignItems="flex-start" gap={0.5}>
              {item.icon}
              <Typography variant="caption" color="text.secondary">{item.label}</Typography>
            </Box>
          ))}
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 20, height: 20, borderRadius: '50%', bgcolor: '#6a1b9a', border: '2px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Typography sx={{ color: 'white', fontSize: 8, fontWeight: 700 }}>A#</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">Amendment</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: '10px solid #f57c00', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">Edit</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Box sx={{ width: 2, height: 14, bgcolor: 'error.main', opacity: 0.5, flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary">Today</Typography>
          </Box>
        </Box>

      </Box>
    </Box>
  );
}
